import { useEffect, useState, useCallback, useRef } from "react";

import { getRefillConfirmation } from "../modules/refill/getRefillConfirmation";
import { confirmRefillSelection } from "../modules/refill/confirmRefillSelection";
import { listPrescriptionRefills } from "../modules/refill/listPrescriptionRefills";
import type { RefillConfirmationProto } from "../modules/refill/types";

import { useAuthCredentials } from "./useAuthCredentials";

/**
 * Fetches refill confirmation data. If no scheduledFillId is provided,
 * discovers the next scheduled fill by listing the patient's refills
 * and picking the first one with a SCHEDULED status.
 */
export const useRefillConfirmation = (
  scheduledFillIdParam: number | null = null
): {
  data: RefillConfirmationProto | null;
  loading: boolean;
  error: string | null;
  confirmSelection: (selectedInternalDrugId: string) => Promise<string | null>;
} => {
  const { accessCode, accessToken } = useAuthCredentials();
  const [data, setData] = useState<RefillConfirmationProto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvedFillIdRef = useRef<number | null>(scheduledFillIdParam);

  const fetchData = useCallback(async () => {
    if (!accessCode) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let fillId = scheduledFillIdParam;
      if (fillId === null) {
        const refills = await listPrescriptionRefills(
          { accessCode },
          accessToken ?? ""
        );
        const scheduled = refills.find((r) => r.status === "SCHEDULED");
        if (!scheduled) {
          setError("No upcoming refill found");
          setLoading(false);
          return;
        }
        fillId = scheduled.id;
      }
      resolvedFillIdRef.current = fillId;

      const response = await getRefillConfirmation(
        { accessCode, scheduledFillId: fillId },
        accessToken ?? ""
      );
      setData(response.refillConfirmation ?? null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch refill data"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessCode, accessToken, scheduledFillIdParam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const confirmSelection = useCallback(
    async (selectedInternalDrugId: string): Promise<string | null> => {
      if (!accessCode || resolvedFillIdRef.current === null) {
        throw new Error("Unable to confirm selection — missing user or fill data");
      }

      const result = await confirmRefillSelection(
        {
          accessCode,
          scheduledFillId: resolvedFillIdRef.current,
          body: { selectedInternalDrugId },
        },
        accessToken ?? ""
      );
      return result.doseLabel;
    },
    [accessCode, accessToken]
  );

  return { data, loading, error, confirmSelection };
};
